package dev.kuml.portal.web.usage

import dev.kuml.portal.web.i18n.format.LocaleFormat
import dev.kuml.portal.web.i18n.strings.UsageStrings

/**
 * Formatting for the global "Nutzungs-/Plan-Indikator" chip (see the design spec, section 4).
 * Pure string formatting, DOM-free, so the full/compact copy and the non-alarming "at limit"
 * accent rule are directly unit-testable.
 *
 * R-18: [full] now takes the active language's [UsageStrings]/[LocaleFormat] instead of a
 * pre-formatted `planLabel` string -- plan-name and unit-word wording moved into those interfaces
 * (see [LocaleFormat.planLabel]/[UsageStrings.projectsUnit]/[UsageStrings.aiGenerationsUnit]).
 * [compact] carries no words at all (only digits and `/`/`·`) and therefore needs no language
 * parameter, unchanged from before this wave.
 */
public object UsageFormat {
    /** Full chip text, e.g. `"Free-Plan · 2/3 Projekte · 5/20 KI-Generierungen"` (German) or
     * `"Free plan · 2/3 projects · 5/20 AI generations"` (English). A `null` limit means unlimited
     * (`"∞"`). */
    public fun full(
        planId: String,
        projectsUsed: Long,
        projectsLimit: Int?,
        aiUsed: Int,
        aiLimit: Int?,
        strings: UsageStrings,
        format: LocaleFormat,
    ): String {
        val projectsPart = "$projectsUsed/${limitLabel(projectsLimit)} ${strings.projectsUnit}"
        val aiPart = "$aiUsed/${limitLabel(aiLimit)} ${strings.aiGenerationsUnit}"
        return "${format.planLabel(planId)} · $projectsPart · $aiPart"
    }

    /** Compact chip text for narrow viewports, e.g. `"2/3 · 5/20"` -- the full text remains
     * available as a tooltip (see the design spec's responsive rule). */
    public fun compact(
        projectsUsed: Long,
        projectsLimit: Int?,
        aiUsed: Int,
        aiLimit: Int?,
    ): String {
        val projects = "$projectsUsed/${limitLabel(projectsLimit)}"
        val ai = "$aiUsed/${limitLabel(aiLimit)}"
        return "$projects · $ai"
    }

    /** `true` once the caller is AT (not only over) the AI-generation limit -- the chip's non-
     * alarming gold accent applies from here (design spec: "nie alarmierend ... wird das je der
     * Fall, sofort zurücknehmen"), never red, never enlarged. */
    public fun isAtAiLimit(
        aiUsed: Int,
        aiLimit: Int?,
    ): Boolean = aiLimit != null && aiUsed >= aiLimit

    private fun limitLabel(limit: Int?): String = limit?.toString() ?: "∞"

    /** The locale-formatted date the monthly AI quota next resets: the first of the month
     * following [period] (`"YYYY-MM"`, as returned by `GET /api/account/usage`). Used by the 402
     * ("Kontingent erschöpft") status copy. */
    public fun nextPeriodStartLabel(
        period: String,
        format: LocaleFormat,
    ): String? {
        val parts = period.split("-")
        if (parts.size != 2) return null
        val year = parts[0].toIntOrNull() ?: return null
        val month = parts[1].toIntOrNull() ?: return null
        if (month !in 1..12) return null
        val nextMonth = if (month == 12) 1 else month + 1
        val nextYear = if (month == 12) year + 1 else year
        return format.dateFromYmd(nextYear, nextMonth, 1)
    }
}
